import React from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaStar, FaGift, FaTrophy, FaUsers, FaClock, FaAward, FaGlobe, FaHandHoldingHeart, FaLeaf } from 'react-icons/fa';

const AboutPage = () => {
    const stats = [
        { number: "10,000+", label: "Happy Children", icon: FaUsers },
        { number: "500+", label: "Unique Toys", icon: FaGift },
        { number: "50+", label: "Countries", icon: FaGlobe },
        { number: "98%", label: "Satisfaction", icon: FaStar }
    ];

    const team = [
        { name: "Sarah Johnson", role: "Founder & CEO", bio: "Former teacher with 15+ years of experience", icon: FaUsers },
        { name: "Michael Chen", role: "Product Curator", bio: "Toy expert and child development specialist", icon: FaUsers },
        { name: "Emma Williams", role: "Customer Happiness", bio: "Ensuring every customer leaves with a smile", icon: FaUsers }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 py-24 overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative container mx-auto px-4 text-center text-white">
                    <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in-up">
                        About TFM Toys
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 animate-fade-in-up animation-delay-200 max-w-2xl mx-auto">
                        Creating magical moments for children through thoughtful, high-quality toys
                    </p>
                </div>
            </div>

            {/* Our Story Section */}
            <div className="container mx-auto px-4 py-20">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Story</h2>
                        <div className="w-24 h-1 bg-purple-600 mx-auto rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                            <p>
                                TFM Toys was founded in 2024 with a simple yet powerful mission: to bring joy, imagination, 
                                and learning to children through high-quality, innovative toys. What started as a small 
                                family business has grown into a trusted destination for parents worldwide.
                            </p>
                            <p>
                                We believe that play is essential to childhood development. That's why we carefully 
                                curate our collection, selecting only those that inspire creativity, encourage 
                                learning, and, most importantly, are fun to play with.
                            </p>
                            <p>
                                Our team is passionate about toys and dedicated to providing exceptional customer service. 
                                We're parents, educators, and toy enthusiasts who understand the importance of finding the 
                                right toy for the right child.
                            </p>
                        </div>
                        <div className="relative">
                            <div className="rounded-2xl overflow-hidden shadow-xl">
                                <img 
                                    src="https://images.unsplash.com/photo-1608889335941-32ac5f2041b9?w=600" 
                                    alt="Children playing with toys"
                                    className="w-full h-96 object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4">
                                <div className="flex items-center gap-3">
                                    <div className="bg-purple-100 rounded-full p-3">
                                        <FaHeart className="text-purple-600 text-2xl" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-800">Made with Love</p>
                                        <p className="text-sm text-gray-600">Since 2024</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mission & Vision */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 py-20">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-xl p-8 text-center transform hover:scale-105 transition duration-300">
                            <div className="text-6xl mb-4">🎯</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-3">Our Mission</h3>
                            <p className="text-gray-600 leading-relaxed">
                                To provide safe, innovative, and engaging toys that spark imagination, 
                                foster learning, and create lasting memories for children of all ages.
                            </p>
                        </div>
                        <div className="bg-white rounded-2xl shadow-xl p-8 text-center transform hover:scale-105 transition duration-300">
                            <div className="text-6xl mb-4">👁️</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-3">Our Vision</h3>
                            <p className="text-gray-600 leading-relaxed">
                                To become the most trusted name in children's toys, known for quality, 
                                innovation, and commitment to child development worldwide.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Core Values */}
            <div className="container mx-auto px-4 py-20">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Core Values</h2>
                    <div className="w-24 h-1 bg-purple-600 mx-auto rounded-full"></div>
                    <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                        These principles guide everything we do
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition">
                        <div className="bg-gradient-to-r from-purple-600 to-pink-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaHeart className="text-3xl text-white" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3">Quality First</h3>
                        <p className="text-gray-600">
                            We never compromise on quality. Every toy meets strict safety standards.
                        </p>
                    </div>
                    <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition">
                        <div className="bg-gradient-to-r from-purple-600 to-pink-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaStar className="text-3xl text-white" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3">Customer Joy</h3>
                        <p className="text-gray-600">
                            Your satisfaction is our priority. We're here to make you smile.
                        </p>
                    </div>
                    <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition">
                        <div className="bg-gradient-to-r from-purple-600 to-pink-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaLeaf className="text-3xl text-white" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3">Sustainability</h3>
                        <p className="text-gray-600">
                            Committed to eco-friendly practices and sustainable materials.
                        </p>
                    </div>
                </div>
            </div>

            {/* Statistics */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 py-20">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center text-white transform hover:scale-105 transition">
                                <stat.icon className="text-4xl mx-auto mb-3" />
                                <div className="text-4xl font-bold mb-2">{stat.number}</div>
                                <div className="text-sm opacity-90">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Team Section */}
            <div className="container mx-auto px-4 py-20">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">Meet Our Team</h2>
                    <div className="w-24 h-1 bg-purple-600 mx-auto rounded-full"></div>
                    <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                        Passionate individuals dedicated to bringing joy to children everywhere
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                    {team.map((member, index) => (
                        <div key={index} className="text-center group">
                            <div className="w-40 h-40 mx-auto mb-4 relative">
                                <div className="w-full h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center group-hover:scale-110 transition duration-300">
                                    <member.icon className="text-6xl text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                            <p className="text-purple-600 mb-2">{member.role}</p>
                            <p className="text-gray-600 text-sm">{member.bio}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-gray-100 py-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose TFM Toys?</h2>
                        <div className="w-24 h-1 bg-purple-600 mx-auto rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                        <div className="bg-white rounded-xl p-6 text-center hover:shadow-xl transition">
                            <div className="text-4xl mb-3">🔒</div>
                            <h3 className="font-semibold mb-2">Safe & Secure</h3>
                            <p className="text-sm text-gray-600">All toys meet safety standards</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 text-center hover:shadow-xl transition">
                            <div className="text-4xl mb-3">🚚</div>
                            <h3 className="font-semibold mb-2">Fast Shipping</h3>
                            <p className="text-sm text-gray-600">Free delivery on orders $50+</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 text-center hover:shadow-xl transition">
                            <div className="text-4xl mb-3">🔄</div>
                            <h3 className="font-semibold mb-2">Easy Returns</h3>
                            <p className="text-sm text-gray-600">30-day return policy</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 text-center hover:shadow-xl transition">
                            <div className="text-4xl mb-3">💝</div>
                            <h3 className="font-semibold mb-2">Gift Wrapping</h3>
                            <p className="text-sm text-gray-600">Free gift wrap available</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="container mx-auto px-4 py-20">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="relative">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Start Shopping?</h2>
                        <p className="text-lg mb-6 opacity-90">Discover our amazing collection of toys today!</p>
                        <Link 
                            to="/products" 
                            className="inline-block bg-white text-purple-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition transform hover:scale-105"
                        >
                            Shop Now →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutPage;